import { useContext, useEffect, useRef, useState } from 'react';
import { TerraDraw, TerraDrawCircleMode, TerraDrawPolygonMode, ValidateNotSelfIntersecting } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { MapContext } from '../MapContext';
import { circleFeatureToGeofence, polygonFeatureToGeofence } from './terraDrawConversions';

// The Google Maps adapter attaches pointer listeners (during start()) to an
// internal map child element: the transparent interaction div Google renders with
// "z-index: 3". That element does not exist until Google Maps has finished
// rendering, so starting TerraDraw too early throws
// "Cannot read properties of null (reading 'addEventListener')".
const MAP_EVENT_ELEMENT_SELECTOR = 'div[style*="z-index: 3;"]';
const MAX_INIT_FRAMES = 600; // ~10s at 60fps before giving up

// Drives TerraDraw for CREATING new geofences. On finish it converts the drawn
// feature into our fences[] model and clears TerraDraw, so committed shapes are
// owned/rendered by GeofenceArtist (not TerraDraw).
const useGeofenceDrawing = ({ addGeofence }) => {
    const { map } = useContext(MapContext);
    const drawRef = useRef(null);
    const activeModeRef = useRef(null);
    // keep the latest addGeofence without re-creating the TerraDraw instance
    const addGeofenceRef = useRef(addGeofence);
    addGeofenceRef.current = addGeofence;

    const [activeMode, setActiveMode] = useState(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!map || !window.google || !window.google.maps) return undefined;

        let cancelled = false;
        let rafId = null;
        let frames = 0;

        const startDrawing = () => {
            const draw = new TerraDraw({
                adapter: new TerraDrawGoogleMapsAdapter({ map, lib: window.google.maps }),
                // ValidateNotSelfIntersecting rejects self-intersecting polygons at draw
                // time (parity with the original isValidPolygon kinks check). editable
                // lets the user drag/adjust nodes while still drawing the polygon.
                modes: [new TerraDrawCircleMode(), new TerraDrawPolygonMode({ validation: ValidateNotSelfIntersecting, editable: true })],
            });

            draw.on('ready', () => {
                if (cancelled) return;
                setReady(true);
            });

            draw.on('finish', (id, context) => {
                if (cancelled || context.action !== 'draw') return; // ignore edit/drag finishes + post-teardown events
                const feature = draw.getSnapshot().find((f) => f.id === id);
                // Choose the converter from the feature's own mode (more reliable than
                // UI state, which can drift if the mode is switched mid-draw).
                const mode = feature?.properties?.mode || activeModeRef.current;
                const fence = feature && (mode === 'circle' ? circleFeatureToGeofence(feature) : polygonFeatureToGeofence(feature));
                if (fence) addGeofenceRef.current(fence.type, fence);
                draw.clear();
                draw.setMode('static');
                activeModeRef.current = null;
                setActiveMode(null);
            });

            draw.start();
            drawRef.current = draw;
        };

        // Wait until Google Maps has rendered the element the adapter binds to,
        // then start TerraDraw. Polling covers every timing case (not rendered yet,
        // rendered after an animation/resize) without racing the map.
        const waitForMapElement = () => {
            if (cancelled) return;
            const mapDiv = map.getDiv();
            if (mapDiv && mapDiv.querySelector(MAP_EVENT_ELEMENT_SELECTOR)) {
                startDrawing();
                return;
            }
            frames += 1;
            if (frames > MAX_INIT_FRAMES) {
                // eslint-disable-next-line no-console
                console.warn('TerraDraw: map interaction element not found; geofence drawing disabled.');
                return;
            }
            rafId = window.requestAnimationFrame(waitForMapElement);
        };
        waitForMapElement();

        return () => {
            cancelled = true;
            if (rafId) window.cancelAnimationFrame(rafId);
            if (drawRef.current) {
                drawRef.current.stop();
                drawRef.current = null;
            }
            setReady(false);
            setActiveMode(null);
            activeModeRef.current = null;
        };
        // Re-create TerraDraw only when the map instance changes; the latest
        // addGeofence is read via a ref so it need not be a dependency.
    }, [map]);

    // mode is 'circle' | 'polygon' | null (null deselects → static)
    const selectMode = (mode) => {
        const draw = drawRef.current;
        if (!draw || !ready) return;
        activeModeRef.current = mode;
        setActiveMode(mode);
        draw.setMode(mode || 'static');
    };

    return { activeMode, selectMode, ready };
};

export default useGeofenceDrawing;
